import { saveCustomerJourneyCookie } from '@cinch-labs/customer-journey-cookies'
import { Heading } from '@cinch-labs/design-system'
import { SsrExperimentsProvider } from '@cinch-nx/adobe-server-side'
import { useStoryblokState } from '@cinch-nx/data-storyblok'
import { Env, readFromEnv } from '@cinch-nx/environments'
import {
  Head,
  MINIMUM_TRUSTPILOT_SCORE,
  StoryblokRenderer,
} from '@cinch-nx/landing-ui-shared'
import { TrustpilotReviews, Wrapper } from '@cinch-nx/shared-ui'

import {
  HelpMeChoose,
  HomeHeroV2,
  OurCommitmentCarousel,
  PartExchangeSection,
  ShopByShape,
  USPContainer,
  ValuePropositions,
  WeBuyAnyCar,
  WhyCinch,
} from './components'
import { NewCustomerTabs } from './components/new-customer-tabs'
import { RecentAndRecommendedVehicles } from './components/recent-and-recommended-vehicles'
import { ReturningCustomerTabs } from './components/returning-customer-tabs'
import {
  carsCarouselFallback,
  fallbackHeroProps,
  helpMeChooseFallback,
  newCarsCarouselTabFallback,
  partExFallback,
  usedCarsCarouselTabFallback,
  valuePropositionsFallBack,
  whyCinchFallback,
} from './data/fallback/homepage-props-fallback'
import { storyblokComponents } from './data/storyblok-renderer-map'
import type { HomepageProps } from './get-server-side-props'
import styles from './homepage.module.css'
import homePage from './locale/index'

export interface FieldOption {
  count: string
  label: string
  value: string
}

export const LandingFeatureHomePage = ({
  preview,
  vehiclesData,
  trustpilotData,
  story,
  showPersonalisedComponents,
  recentlyViewedListings,
  similarListings,
  pageMeta,
  searchResponse,
}: HomepageProps) => {
  const storyProp = story ? story : undefined
  story = useStoryblokState({ story: storyProp, enableBridge: preview })
  const { ourCommitmentSlides, wbac, trustpilotReviews, seo } = homePage

  if (preview && story.content.previewPersonalisedContent) {
    showPersonalisedComponents = story.content.previewPersonalisedContent
  }

  const personalised = story?.content?.personalised?.[0]
  saveCustomerJourneyCookie()

  const isProd = process.env['STAGE_NAME'] === 'production'
  const shouldShowFallback = !story || !story.content?.herov2
  // TODO: uncomment when continue with favoriting on home when A&E is back
  // const showFavouritingTab =
  //   readFromEnv(Env.EnableFavouritingHomePageChanges) === 'true'
  // const { status: userStatus } = useUserStore()
  // const { favourites } = useFavouritesStore()
  // this console.logs are required in the ticket to prove we are getting favourites and user Status.
  // console.log('userStatus', userStatus)
  // console.log('favourites', favourites)

  const newCarsIsEnabled = readFromEnv(Env.EnableNewCars) === 'true'

  return (
    <SsrExperimentsProvider experiments={pageMeta.experiments}>
      <Head
        {...story}
        fallbackTitle={seo.title}
        fallbackDescription={seo.description}
      />
      {shouldShowFallback ? (
        <HomeHeroV2 {...fallbackHeroProps} />
      ) : (
        story.content.herov2 && (
          <StoryblokRenderer
            block={story.content.herov2}
            components={storyblokComponents}
            props={{
              makes: vehiclesData.makes,
              totalStock: vehiclesData.totalStock,
              starRating: trustpilotData.stars,
              starRatingLabel: trustpilotData.starRatingLabel,
              numberOfReviews: trustpilotData.total,
              searchResponse,
            }}
          />
        )
      )}
      {story && story?.content?.usp && (
        <USPContainer slides={story.content.usp} />
      )}
      {/* TODO: uncomment when continue with favouriting on home when A&E is back */}
      {/* {showFavouritingTab && (
        <div className={styles['favouritingTabText']}>
          Favouriting feature is enabled!
        </div>
      )} */}
      {newCarsIsEnabled && !showPersonalisedComponents && (
        <Wrapper className={{ inner: styles['newUserTabbedCarousel'] }}>
          <NewCustomerTabs
            usedCarsCarouselTab={
              story?.content?.usedCarsCarouselTab?.[0] ??
              usedCarsCarouselTabFallback
            }
            newCarsCarouselTab={
              story?.content?.newCarsCarouselTab?.[0] ??
              newCarsCarouselTabFallback
            }
          />
        </Wrapper>
      )}
      {showPersonalisedComponents ? (
        <Wrapper>
          <Heading element="h2" className={styles['heading']}>
            Welcome back
          </Heading>
          {newCarsIsEnabled ? (
            <ReturningCustomerTabs
              newCarsTab={
                story?.content?.newCarsCarouselTab?.[0] ??
                newCarsCarouselTabFallback
              }
            />
          ) : (
            <RecentAndRecommendedVehicles
              recentlyViewedListings={recentlyViewedListings ?? []}
              similarListings={similarListings ?? []}
            />
          )}

          <div className={styles['valuePropsCardsContainer']}>
            {story && personalised.valuePropositions ? (
              <StoryblokRenderer
                block={personalised.valuePropositions}
                components={storyblokComponents}
              />
            ) : (
              <>
                {valuePropositionsFallBack.map((proposition, index) => (
                  <ValuePropositions key={index} {...proposition} />
                ))}
              </>
            )}
          </div>
          {story && personalised.helpMeChoose ? (
            <StoryblokRenderer
              block={personalised.helpMeChoose}
              components={storyblokComponents}
            />
          ) : (
            <HelpMeChoose {...helpMeChooseFallback} />
          )}
        </Wrapper>
      ) : null}
      <ShopByShape count={vehiclesData.carTypes} />
      {!showPersonalisedComponents ? (
        story && story?.content?.whyCinch ? (
          <StoryblokRenderer
            block={story?.content?.whyCinch}
            components={storyblokComponents}
          />
        ) : (
          <WhyCinch {...whyCinchFallback} />
        )
      ) : null}
      {story && story?.content?.partEx ? (
        <StoryblokRenderer
          block={story?.content?.partEx}
          components={storyblokComponents}
        />
      ) : (
        <PartExchangeSection {...partExFallback} />
      )}

      {newCarsIsEnabled && (
        <Wrapper>
          <StoryblokRenderer
            block={story?.content?.carsCarousel ?? carsCarouselFallback}
            components={storyblokComponents}
          />
        </Wrapper>
      )}

      {trustpilotData?.trustScore >= MINIMUM_TRUSTPILOT_SCORE && (
        <TrustpilotReviews
          title={trustpilotReviews.title}
          starRatingLabel={trustpilotData.starRatingLabel}
          numberOfReviews={trustpilotData.total}
          reviews={trustpilotData.reviews}
        />
      )}

      {!showPersonalisedComponents ? (
        <OurCommitmentCarousel {...ourCommitmentSlides} />
      ) : null}
      <WeBuyAnyCar {...wbac} />
      {isProd ? (
        <img
          alt="ZeroFox"
          className={styles['zeroFox']}
          src="https://d21y75miwcfqoq.cloudfront.net/786a6533"
        />
      ) : null}
    </SsrExperimentsProvider>
  )
}

export default LandingFeatureHomePage
